import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	type PositionStatus,
	positionStatusesQuery,
} from '@atlassian/search-client/positionStatus';
import { fetchOptionsAcrossSites } from '@atlassian/search-common/fetch-options-across-sites';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

// Number of results returned in dropdown
const LIMIT = 20;

const StatusMapping: Record<string, string> = {
	Filled: 'filled',
	'Not filled': 'unfilled',
};

const ReverseStatusMapping: Record<string, string> = Object.fromEntries(
	Object.entries(StatusMapping).map(([key, value]) => [value, key]),
);

export const mapPositionStatusResult: (
	positionStatus: PositionStatus,
	product: ProductKeys,
) => FetchedSelectFilterOption = (
	positionStatus: PositionStatus,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	const value = StatusMapping[positionStatus.node.statusValue || ''] || 'unrecognized';
	return {
		trackingKey: value,
		label: positionStatus.node.statusDisplayValue || '',
		value: value,
		queryParamValue: value,
		product: product,
	};
};

/**
 * Queries position statuses by search term
 * @param query
 * @param config
 * @returns
 */
const fetchPositionStatusesForSite = async (
	rqlQuery: string,
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionStatusesResponse = await positionStatusesQuery(
		cloudId,
		LIMIT,
		rqlQuery,
		undefined,
		headers,
	);
	const positionStatuses: PositionStatus[] =
		positionStatusesResponse.data?.radar_fieldValues.edges ?? [];

	return positionStatuses.map((positionStatus) =>
		mapPositionStatusResult(positionStatus, config.product as ProductKeys),
	);
};

const fetchPositionStatuses = async (
	rqlQuery: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	return fetchOptionsAcrossSites(config, (cloudId) =>
		fetchPositionStatusesForSite(rqlQuery, config, cloudId),
	);
};

const searchPositionStatuses = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `status ~ '${query}'`;
	return fetchPositionStatuses(rqlQuery, config);
};

/**
 * Queries all selected position statuses by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `status in ('${queryParams
		.map((param) => ReverseStatusMapping[param || 'unrecognized'])
		.filter(Boolean)
		.join("', '")}')`;
	return fetchPositionStatuses(rqlQuery, config);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter: Readonly<{
	id: 'positionStatus';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'positionStatus'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionStatus'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionStatus'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'positionStatus',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionStatuses',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionStatuses('', config);
		},
	},
	lookup: {
		fetcher: searchPositionStatuses,
	},
});
